import type { RefObject } from 'react';
import { CalendarDay, Period, PeriodText } from '@/components/calendar/calendar.type';
import { WEEK_DAY } from '@/components/calendar/utils';
import Converter from '@/utils/converter';
import Time from '@/utils/time';

export function isCalendarDayEqualsToDate(calendarDay: CalendarDay, date: Date | null) {
  return (
    !!date &&
    date.getDate() === calendarDay.day &&
    date.getMonth() === calendarDay.month &&
    date.getFullYear() === calendarDay.year
  );
}

export function getYearButtonRef<T>(
  isMonth: boolean,
  [buttonYear, selectedYear, startYear]: [string, string, string],
  [selectedRef, startRef]: [RefObject<T>, RefObject<T>],
) {
  if (!isMonth && buttonYear === selectedYear) return selectedRef;
  if (!isMonth && buttonYear === startYear) return startRef;
  return null;
}

export function scroll(element: HTMLElement, parent: HTMLElement): void {
  const clientHeight = element.offsetParent?.clientHeight || 0;
  const buttonTop = element.offsetTop;
  const buttonStyles = window.getComputedStyle(element);
  const marginTop = parseInt(buttonStyles.getPropertyValue('margin-top'));
  const marginBottom = parseInt(buttonStyles.getPropertyValue('margin-bottom'));
  const buttonHeight = element.offsetHeight + Math.ceil((marginTop + marginBottom) / 2);
  parent.scrollTo({
    top: buttonTop - Math.ceil(clientHeight / 2) + buttonHeight,
    behavior: 'smooth',
  });
}

export function getNextIndex(array: unknown[], index: number, direction: 'forward' | 'backward') {
  if (array.length === 0 || index < 0 || index >= array.length) return -1;
  if (direction === 'forward') {
    return (index + 1) % array.length;
  } else {
    return (index - 1 + array.length) % array.length;
  }
}

export function isNumberInRange([start, end]: [number, number], num: number) {
  return num >= start && num <= end;
}

export function isDateInPeriod(date: Date | null, periodText: PeriodText): boolean {
  const [start, end] = calculatePeriod(periodText);
  if (!date) return false;
  return date >= start && date <= end;
}

export function adjustDateToPeriod(date: Date | null, init: Date, periodText: PeriodText) {
  const [start, end] = calculatePeriod(periodText);
  const checkDate = date || init;
  if (checkDate) {
    const isWithinPeriod = isDateInPeriod(checkDate, periodText);
    if (isWithinPeriod) {
      return checkDate === date ? date : init;
    } else {
      return checkDate < start ? start : end;
    }
  } else {
    return start;
  }
}

export function getDateFromToday(daysFromToday = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return Converter.Date.ToDateString(date);
}

export function getDaysInMonth(month: number, year: number): number {
  const currentDate = Time.date(1, month, year);
  currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
  currentDate.setUTCDate(0);
  return currentDate.getDate();
}

export function getDaysInPreviousMonth(month: number, year: number): number {
  return getDaysInMonth(month - 1, year);
}

export function generateCalendar(
  weekStart: WEEK_DAY,
  [month, year]: [number, number],
  periodText: PeriodText,
): CalendarDay[][] {
  const [start, end] = calculatePeriod(periodText);
  const date = Time.date(1, month, year);
  const firstDay = date.getDay();
  const daysInMonth = getDaysInMonth(month, year);
  const daysInPreviousMonth = getDaysInPreviousMonth(month, year);
  const previousMonth = month === 0 ? 11 : month - 1;
  const previousYear = month === 0 ? year - 1 : year;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;

  const calendar: CalendarDay[][] = [];
  let week: CalendarDay[] = [];
  let day = 1;

  let offset = (firstDay - weekStart + 7) % 7;

  if (offset < 0) {
    offset += 7;
  }

  for (let i = offset; i > 0; i--) {
    const currentDate = Time.date(daysInPreviousMonth - i + 1, previousMonth, previousYear);
    week.push({
      outOfPeriod: currentDate < start || currentDate > end,
      notThisMonth: true,
      day: daysInPreviousMonth - i + 1,
      year: previousYear,
      month: previousMonth,
    });
  }

  while (day <= daysInMonth) {
    const currentDate = Time.date(day, month, year);
    week.push({
      outOfPeriod: currentDate < start || currentDate > end,
      notThisMonth: false,
      day,
      year,
      month,
    });
    day++;

    if (week.length === 7) {
      calendar.push(week);
      week = [];
    }
  }

  if (week.length > 0) {
    let nextMonthDay = 1;
    while (week.length < 7) {
      const currentDate = Time.date(nextMonthDay, nextMonth, nextYear);
      week.push({
        outOfPeriod: currentDate < start || currentDate > end,
        notThisMonth: true,
        day: nextMonthDay,
        year: nextYear,
        month: nextMonth,
      });
      nextMonthDay++;
    }
    calendar.push(week);
  }

  return calendar;
}

export function getYearsBetween(periodText: PeriodText): number[] {
  const [start, end] = calculatePeriod(periodText);
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  if (start > end) {
    return [];
  } else {
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  }
}

function calculatePeriod(periodText: PeriodText): Period {
  if (periodText.length === 2) {
    return periodText.map((p, index) => Converter.DateRelative.ToDate(p, index <= 0)) as Period;
  } else {
    const from = Time.startOfDay(new Date());
    const to = Time.endOfDay(new Date());
    from.setFullYear(from.getFullYear() - 200);
    to.setFullYear(to.getFullYear() + 200);
    return [from, to];
  }
}
